import React, { useEffect, useRef, useState } from "react";
import { API, TOKEN_KEY, errMsg } from "@/lib/api";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Empty } from "@/components/Primitives";
import { toast } from "sonner";
import { Sparkles, SendHorizonal, Trash2, Loader2, User2, Square } from "lucide-react";

const inline = (text, keyPrefix) => {
  const nodes = [];
  const re = /(\*\*[^*]+\*\*|\[\d{2}:\d{2}:\d{2}\])/g;
  let last = 0;
  let m;
  while ((m = re.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    const tok = m[0];
    if (tok.startsWith("**")) {
      nodes.push(<strong key={`${keyPrefix}-b-${m.index}`} className="font-semibold text-slate-900">{tok.slice(2, -2)}</strong>);
    } else {
      nodes.push(<span key={`${keyPrefix}-t-${m.index}`} className="font-mono text-[11px] text-brand-600">{tok}</span>);
    }
    last = m.index + tok.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

const RichText = ({ text }) => {
  const lines = (text || "").split("\n");
  return (
    <div className="space-y-1.5">
      {lines.map((raw, i) => {
        const trimmed = raw.trim();
        if (!trimmed) return <div key={i} className="h-1" />;
        const bullet = /^([*\-•]|\d+\.)\s+/.exec(trimmed);
        if (bullet) {
          const indent = /^\s{3,}/.test(raw) ? "ml-5" : "";
          return (
            <div key={i} className={`flex gap-2.5 ${indent}`}>
              <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full grad-brand" />
              <span>{inline(trimmed.slice(bullet[0].length), `l${i}`)}</span>
            </div>
          );
        }
        if (/^#{1,4}\s/.test(trimmed))
          return <p key={i} className="font-display font-semibold text-slate-900">{inline(trimmed.replace(/^#+\s/, ""), `l${i}`)}</p>;
        return <p key={i}>{inline(trimmed, `l${i}`)}</p>;
      })}
    </div>
  );
};

const Bubble = ({ role, content, streaming, testId }) => {
  const isUser = role === "user";
  return (
    <div className={`flex gap-3 ${isUser ? "flex-row-reverse" : ""}`} data-testid={testId}>
      <span className={`grid place-items-center h-8 w-8 shrink-0 rounded-xl ${isUser ? "bg-slate-200 text-slate-600" : "grad-brand text-white"}`}>
        {isUser ? <User2 size={15} /> : <Sparkles size={15} />}
      </span>
      <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
        isUser ? "bg-slate-100 text-slate-800 whitespace-pre-wrap" : "bg-white border border-slate-200/70 text-slate-700 shadow-clay"}`}>
        {isUser ? content : <RichText text={content} />}
        {streaming && <span className="ml-1 inline-block h-4 w-[2px] translate-y-0.5 bg-brand-600 animate-pulse" />}
      </div>
    </div>
  );
};

export const MeetingChat = ({ meetingId, hasTranscript }) => {
  const [messages, setMessages] = useState([]);
  const [suggested, setSuggested] = useState([]);
  const [q, setQ] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [partial, setPartial] = useState("");
  const scrollRef = useRef(null);
  const abortRef = useRef(null);

  useEffect(() => {
    api.get(`/meetings/${meetingId}/chat`)
      .then(({ data }) => { setMessages(data.messages); setSuggested(data.suggested); })
      .catch((e) => toast.error(errMsg(e)));
  }, [meetingId]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, partial]);

  const send = async (text) => {
    const question = (text ?? q).trim();
    if (!question || streaming) return;
    setQ("");
    setMessages((p) => [...p, { id: `local-${Date.now()}`, role: "user", content: question }]);
    setStreaming(true);
    setPartial("");

    const controller = new AbortController();
    abortRef.current = controller;
    let acc = "";
    try {
      const res = await fetch(`${API}/meetings/${meetingId}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem(TOKEN_KEY)}` },
        body: JSON.stringify({ question }),
        signal: controller.signal,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || `HTTP ${res.status}`);
      }
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n\n");
        buffer = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const payload = JSON.parse(line.slice(6));
          if (payload.delta) { acc += payload.delta; setPartial(acc); }
          if (payload.error) throw new Error(payload.error);
        }
      }
      if (acc) setMessages((p) => [...p, { id: `a-${Date.now()}`, role: "assistant", content: acc }]);
    } catch (e) {
      if (e.name !== "AbortError") toast.error(e.message || "Gagal memuat jawaban");
      if (acc) setMessages((p) => [...p, { id: `a-${Date.now()}`, role: "assistant", content: acc }]);
    } finally {
      setStreaming(false);
      setPartial("");
      abortRef.current = null;
    }
  };

  const clear = async () => {
    try { await api.delete(`/meetings/${meetingId}/chat`); setMessages([]); toast.success("Riwayat chat dihapus"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  if (!hasTranscript) {
    return (
      <div className="clay">
        <Empty icon={Sparkles} title="Transkrip belum tersedia"
          sub="AI Chat membaca transkrip rapat ini. Unggah rekaman audio atau jalankan sesi live terlebih dahulu." />
      </div>
    );
  }

  return (
    <div className="clay flex flex-col overflow-hidden" data-testid="meeting-chat-panel">
      <div className="flex items-center justify-between gap-4 border-b border-slate-100 px-6 py-5">
        <div className="flex items-center gap-3">
          <span className="grid place-items-center h-9 w-9 rounded-xl grad-brand text-white"><Sparkles size={16} /></span>
          <div>
            <p className="font-display text-base font-semibold text-slate-900">AI Chat with Transcript</p>
            <p className="text-xs text-slate-500">Tanya apa saja tentang rapat ini · <span className="font-mono">gemini-3-flash-preview</span></p>
          </div>
        </div>
        {messages.length > 0 && (
          <Button variant="ghost" size="sm" onClick={clear} className="text-slate-500" data-testid="clear-chat-btn">
            <Trash2 size={15} className="mr-1.5" /> Bersihkan
          </Button>
        )}
      </div>

      <div ref={scrollRef} className="flex-1 space-y-5 overflow-y-auto px-6 py-6 min-h-[320px] max-h-[520px] bg-slate-50/40">
        {messages.length === 0 && !partial ? (
          <div className="py-6 text-center">
            <div className="mx-auto grid place-items-center h-12 w-12 rounded-2xl bg-brand-50 text-brand-600"><Sparkles size={22} /></div>
            <p className="mt-4 font-display text-lg font-semibold text-slate-800">Tanyakan apa pun tentang rapat ini</p>
            <p className="mt-2 text-sm text-slate-500">Jawaban selalu mengutip timestamp dan nama pembicara dari transkrip.</p>
          </div>
        ) : (
          messages.map((m) => <Bubble key={m.id} role={m.role} content={m.content} testId={`chat-msg-${m.role}-${m.id}`} />)
        )}
        {partial && <Bubble role="assistant" content={partial} streaming testId="chat-msg-streaming" />}
        {streaming && !partial && (
          <div className="flex items-center gap-2 text-sm text-slate-400" data-testid="chat-thinking">
            <Loader2 size={14} className="animate-spin" /> Menelaah transkrip…
          </div>
        )}
      </div>

      {messages.length === 0 && (
        <div className="flex flex-wrap gap-2 border-t border-slate-100 px-6 pt-5">
          {suggested.map((s, i) => (
            <button key={i} onClick={() => send(s)} disabled={streaming}
              className="rounded-full border border-slate-200 px-3.5 py-1.5 text-xs text-slate-600 hover:border-brand-600 hover:bg-brand-50/60 hover:text-brand-700 transition-colors disabled:opacity-50"
              data-testid={`chat-suggestion-${i}`}>
              {s}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={(e) => { e.preventDefault(); send(); }} className="flex items-end gap-3 px-6 py-5" data-testid="chat-form">
        <Textarea
          value={q} onChange={(e) => setQ(e.target.value)} rows={1}
          onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Contoh: Apa keputusan utama rapat ini dan siapa PIC-nya?"
          className="min-h-[46px] max-h-32 resize-none rounded-xl" data-testid="chat-input"
        />
        {streaming ? (
          <Button type="button" onClick={() => abortRef.current?.abort()} className="h-[46px] shrink-0 rounded-xl bg-slate-800 px-5 text-white hover:bg-slate-900" data-testid="chat-stop-btn">
            <Square size={15} />
          </Button>
        ) : (
          <Button type="submit" disabled={!q.trim()} className="h-[46px] shrink-0 rounded-xl grad-brand px-5 text-white" data-testid="chat-send-btn">
            <SendHorizonal size={16} />
          </Button>
        )}
      </form>
    </div>
  );
};
